import { useRef, useState } from "react";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Download, FileSpreadsheet, Upload, Check } from "lucide-react";
import sparePartsApi from "../../../api/spareParts.api";
import { createUploadBatches, uploadInstructions } from "./bulkUploadClient";

type Kind = "categories" | "spare-parts" | "suppliers" | "purchase-bills";
interface RowResult { rows: number[]; label: string; status: "ready" | "created" | "error" | "unknown"; message: string; code?: string; }
interface Report { totalRows: number; totalRecords: number; created: number; valid: number; errors: number; results: RowResult[]; }
const saveBlob = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a"); link.href = url; link.download = name; link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function BulkUploadButton({ kind, onImported }: { kind: Kind; onImported: () => unknown }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);
  const [progress, setProgress] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const request = async (action: "validate" | "import", uploadFile: Blob = file!): Promise<Report> => {
    const data = new FormData(); data.append("file", uploadFile, file!.name);
    const response = await sparePartsApi.post(`/bulk-upload/${kind}/${action}`, data, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 });
    return response.data.data;
  };
  const validate = async () => {
    if (!file) return;
    setBusy(true); setError(""); setReport(null); setCompleted(false); setProgress(0);
    try { setReport(await request("validate")); }
    catch (err: any) { setError(err?.response?.data?.message || "Unable to validate the workbook."); }
    finally { setBusy(false); }
  };
  const importFile = async () => {
    if (!report || report.errors || completed || !file) return;
    setBusy(true); setError(""); setProgress(0);
    const merged = report.results.map((row) => ({ ...row }));
    let pendingRows: number[] = [];
    try {
      const uploads = await createUploadBatches(file, merged);
      for (let index = 0; index < uploads.length; index += 1) {
        const upload = uploads[index];
        pendingRows = upload.originalRows;
        const batch = await request("import", upload.file);
        pendingRows = [];
        for (const result of batch.results) {
          const originalRows = result.rows.map((number) => upload.originalRows[number - 2]);
          const position = merged.findIndex((row) => row.rows[0] === originalRows[0]);
          if (position >= 0) merged[position] = { ...result, rows: originalRows };
        }
        setReport({ ...report, results: [...merged], created: merged.filter((row) => row.status === "created").length, errors: merged.filter((row) => row.status === "error").length });
        setProgress((index + 1) / uploads.length * 100);
        if (batch.errors) { setError("Import stopped. Review the results and retry only uncreated records in a corrected workbook."); break; }
      }
    } catch (err: any) {
      for (const row of merged) {
        if (pendingRows.includes(row.rows[0])) { row.status = "unknown"; row.message = "Outcome unknown. Check existing records before retrying."; }
      }
      setReport({ ...report, results: [...merged], created: merged.filter((row) => row.status === "created").length, errors: merged.filter((row) => row.status === "error" || row.status === "unknown").length });
      setError(err?.response?.data?.message || "Connection interrupted. The last batch outcome is unknown; refresh the page and check existing records before retrying.");
    } finally {
      setCompleted(true); setBusy(false);
      try { await onImported(); }
      catch { setError("Import processed, but refreshing the list failed. Refresh the page before retrying."); }
    }
  };
  const downloadTemplate = async () => {
    setBusy(true); setError(""); setProgress(0);
    try {
      const response = await sparePartsApi.get(`/bulk-upload/${kind}/template`, { responseType: "blob" });
      saveBlob(response.data, `${kind}-template.xlsx`);
    } catch { setError("Unable to download the template. Check your access and try again."); }
    finally { setBusy(false); }
  };
  const downloadResults = () => {
    const escape = (value: string) => `"${(/^[\s]*[=+@-]/.test(value) ? "'" + value : value).replace(/"/g, '""')}"`;
    const csv = ["Rows,Record,Status,Code,Message", ...(report?.results || []).map((row) => [row.rows.join(" / "), row.label, row.status, row.code || "", row.message].map(escape).join(","))].join("\r\n");
    saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${kind}-upload-results.csv`);
  };
  return <>
    <Button variant="outlined" startIcon={<Upload size={18} />} onClick={() => { setOpen(true); setFile(null); setReport(null); setError(""); setCompleted(false); }}>Bulk Upload</Button>
    <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} fullWidth maxWidth="md">
      <DialogTitle>Bulk Upload: {kind.replace(/-/g, " ")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box component="section" sx={{ bgcolor: "action.hover", p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>Upload Instructions</Typography>
            <Typography variant="body2" mt={1}>Excel .xlsx only. Maximum 500 data rows per upload, excluding the header, and a maximum file size of 10 MB.</Typography>
            <Box component="ol" sx={{ my: 1, pl: 3, fontSize: "0.875rem" }}>
              <li>Download the template and fill its Data sheet. Keep sheet names and column headers unchanged.</li>
              <li>Use YYYY-MM-DD or Excel date cells, plain numbers and text. Do not use formulas; keep optional cells blank.</li>
              <li>Choose the file and click Validate. Correct every reported error before importing.</li>
              <li>Click Import once and keep this dialog open until processing finishes. Download Results afterwards.</li>
            </Box>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}><strong>Required fields:</strong> {uploadInstructions[kind].required}</Typography>
            <Typography variant="body2" mt={1}>{uploadInstructions[kind].rules}</Typography>
            <Typography variant="body2" mt={1}>Existing records are never overwritten. After a failed import, retry only records that were not created. If a batch outcome is unknown, check existing records first.</Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {completed && report && !error && <Alert severity={report.created === report.totalRecords ? "success" : "warning"}>{report.created} of {report.totalRecords} records created.</Alert>}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button disabled={busy} startIcon={<Download size={18} />} onClick={downloadTemplate}>Download Template</Button>
            <Button disabled={busy} variant="outlined" startIcon={<FileSpreadsheet size={18} />} onClick={() => input.current?.click()}>Choose Excel File</Button>
            <Button disabled={busy || !file} startIcon={<Check size={18} />} onClick={validate}>Validate</Button>
          </Stack>
          <input ref={input} hidden type="file" accept=".xlsx" onClick={(event) => { event.currentTarget.value = ""; }} onChange={(event) => {
            const selected = event.target.files?.[0] || null;
            setReport(null); setCompleted(false); setError("");
            if (selected && (!selected.name.toLowerCase().endsWith(".xlsx") || selected.size > 10 * 1024 * 1024)) { setFile(null); setError("Choose an .xlsx workbook under 10 MB."); return; }
            setFile(selected);
          }} />
          {file && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{file.name}</Typography>}
          {busy && <LinearProgress variant={progress > 0 ? "determinate" : "indeterminate"} value={progress} />}
          {report && <>
            <Stack direction="row" flexWrap="wrap" gap={1}><Chip label={`${report.totalRows} rows`} /><Chip label={`${report.totalRecords} records`} /><Chip color="success" label={`${report.created} created`} /><Chip color={report.errors ? "error" : "default"} label={`${report.errors} errors`} /></Stack>
            <TableContainer component={Box} sx={{ maxHeight: 380 }}><Table stickyHeader size="small"><TableHead><TableRow>{["Rows", "Record", "Status", "Result"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead><TableBody>{report.results.map((row) => <TableRow key={row.rows[0]}><TableCell>{row.rows.join(", ")}</TableCell><TableCell sx={{ overflowWrap: "anywhere" }}>{row.label}</TableCell><TableCell>{completed && row.status === "ready" ? "Not imported" : row.status}</TableCell><TableCell sx={{ overflowWrap: "anywhere" }}>{row.code || row.message}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
          </>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        {report && <Button disabled={busy} startIcon={<Download size={18} />} onClick={downloadResults}>Export Results</Button>}
        <Button disabled={busy} onClick={() => setOpen(false)}>Close</Button>
        <Button variant="contained" startIcon={<Upload size={18} />} disabled={busy || !report || report.errors > 0 || completed} onClick={importFile}>Import</Button>
      </DialogActions>
    </Dialog>
  </>;
}
